import { Contact, ContactChannel, ContactChannelType } from './types/Contact';

export const contactChannelTypeOptions: ContactChannelType[] = ['phone', 'whatsApp', 'instagram', 'telegram', 'messenger', 'tikTok', 'signal', 'other'];
export const manuallyManagedContactChannelTypeOptions: ContactChannelType[] = contactChannelTypeOptions;

export const isContactChannelManuallyEditable = (_type: ContactChannelType): boolean => true;

export const getContactChannelTypeLabel = (type: ContactChannelType): string => {
  switch (type) {
    case 'phone':
      return 'Phone';
    case 'whatsApp':
      return 'WhatsApp';
    case 'instagram':
      return 'Instagram';
    case 'telegram':
      return 'Telegram';
    case 'messenger':
      return 'Messenger';
    case 'tikTok':
      return 'TikTok';
    case 'signal':
      return 'Signal';
    default:
      return 'Other';
  }
};

export const createEmptyContactChannel = (type: ContactChannelType = 'phone'): ContactChannel => ({
  type,
  label: '',
  value: '',
  isPrimary: true,
});

export const normalizeWhatsAppPhone = (value: string): string | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;

  const normalized = trimmed.replace(/[\s()+-]/g, '');
  return /^\+?\d{7,15}$/.test(trimmed) || /^\d{7,15}$/.test(normalized)
    ? normalized.replace(/^\+/, '')
    : null;
};

export const sortContactChannels = (channels: ContactChannel[]): ContactChannel[] => {
  return [...channels].sort((left, right) => {
    if (left.isPrimary !== right.isPrimary) return left.isPrimary ? -1 : 1;
    return `${left.type}:${left.label}:${left.value}`.localeCompare(`${right.type}:${right.label}:${right.value}`);
  });
};

export const ensurePrimaryContactChannels = (channels: ContactChannel[]): ContactChannel[] => {
  const sorted = sortContactChannels(channels.filter(channel => channel.value.trim().length > 0 || channel.label.trim().length > 0));
  const seenPrimary = new Set<ContactChannelType>();

  return sorted.map(channel => {
    if (!seenPrimary.has(channel.type)) {
      seenPrimary.add(channel.type);
      return { ...channel, isPrimary: true };
    }

    return { ...channel, isPrimary: false };
  });
};

export const getPrimaryChannelByType = (channels: ContactChannel[], type: ContactChannelType): ContactChannel | null => {
  return sortContactChannels(channels).find(channel => channel.type === type) ?? null;
};

export const getPreferredChannel = (channels: ContactChannel[]): ContactChannel | null => {
  return getPrimaryChannelByType(channels, 'whatsApp')
    ?? getPrimaryChannelByType(channels, 'phone')
    ?? sortContactChannels(channels)[0]
    ?? null;
};

export const contactMatchesChannelTypes = (contact: Contact, selectedTypes: ContactChannelType[]): boolean => {
  if (selectedTypes.length === 0) {
    return true;
  }

  return contact.channels.some(channel => channelMatchesFilterTypes(channel, selectedTypes));
};

const channelMatchesFilterTypes = (channel: ContactChannel, selectedTypes: ContactChannelType[]): boolean => {
  if (selectedTypes.includes(channel.type)) {
    return true;
  }

  if (channel.type !== 'other') {
    return false;
  }

  const label = channel.label.trim().toLowerCase();
  if (selectedTypes.includes('instagram') && label === 'instagram') {
    return true;
  }

  if (selectedTypes.includes('messenger') && label === 'messenger') {
    return true;
  }

  if (selectedTypes.includes('tikTok') && label === 'tiktok') {
    return true;
  }

  if (selectedTypes.includes('other')
    && label.length > 0
    && !['instagram', 'messenger', 'tiktok', 'webchat', 'profile_pic', 'inbox', 'gender', 'timezone'].includes(label)) {
    return true;
  }

  return false;
};

export const getChannelLink = (channel: ContactChannel): string | null => {
  const storedLink = channel.link?.trim();
  if (storedLink) {
    return storedLink;
  }

  const trimmedValue = channel.value.trim();
  if (!trimmedValue) return null;

  const normalizedLabel = channel.label.trim().toLowerCase();

  if ((normalizedLabel === 'profile_pic' || normalizedLabel === 'inbox') && /^https?:\/\//i.test(trimmedValue)) {
    return trimmedValue;
  }

  if (channel.type === 'other' && normalizedLabel === 'instagram' && !/^\d+$/.test(trimmedValue)) {
    const username = trimmedValue.replace(/^@/, '');
    return username ? `https://instagram.com/${encodeURIComponent(username)}` : null;
  }

  switch (channel.type) {
    case 'phone':
      return `tel:${trimmedValue}`;
    case 'whatsApp': {
      const phone = normalizeWhatsAppPhone(trimmedValue);
      return phone ? `https://wa.me/${phone}` : null;
    }
    case 'instagram': {
      if (/^\d+$/.test(trimmedValue)) return null;
      const username = trimmedValue.replace(/^@/, '');
      return username ? `https://instagram.com/${encodeURIComponent(username)}` : null;
    }
    case 'telegram': {
      if (/^https?:\/\//i.test(trimmedValue)) return trimmedValue;
      const username = trimmedValue.replace(/^@/, '').trim();
      return username ? `https://t.me/${encodeURIComponent(username)}` : null;
    }
    case 'messenger':
      return storedLink ?? null;
    default:
      return null;
  }
};

export const getChannelSummary = (channels: ContactChannel[]): string => {
  const sorted = sortContactChannels(channels);
  if (sorted.length === 0) return '-';

  return sorted
    .slice(0, 2)
    .map(channel => channel.label.trim().length > 0 ? `${channel.label}: ${channel.value}` : channel.value)
    .join(' · ');
};